import { Component, Vue, Prop } from 'vue-property-decorator';
import { CsvDownload, AgriFieldGroupIncomeOutgoResponse } from '@/types/APIResponse';
import { AgriFieldGroupIncomeOutgo, AgriFieldGroupIncomeOutgoOutline } from '@/types/Component';
import IncomeAndOutgoChart from '@/components/IncomeAndOutgo/IncomeAndOutgoChart/IncomeAndOutgoChart.vue';
import AgriFieldGroupModal from '@/assets/common/Modal/AgriFieldGroupModal/AgriFieldGroupModal.vue'
import { toCamel } from "snake-camel";

@Component({
    filters: {
        applyNumberFixed(value: number): string | null {
            return Number(value).toFixed(3).toLocaleString()
        },
        changeNumberFormat(value: number): string | null {
            return Math.round(value).toLocaleString().replace(/^-/, '▲')
        }
    },
    components: {
        IncomeAndOutgoChart,
        AgriFieldGroupModal
    }
})

export default class IncomeAndOutgoAgriFieldGroup extends Vue {

    private responseItems: Array<AgriFieldGroupIncomeOutgoOutline> = []
    private is10aView = false
    private isChartView = false
    private sortByIncomeIsAsc = false
    private sortByOutgoIsAsc = false
    private sortByAllIsAsc = false
    private sortByFieldIsAsc = false
    private sortByDisposalIsAsc = false
    private currentSortKey: 'crops' | 'income' | 'outgo' | 'total' = 'crops'

    private startDateInput: string = this.$parent.$data.startDate
    private endDateInput: string = this.$parent.$data.endDate

    private showAgriFieldGroupModal = false
    private selectedGroupId = ""
    private sortType = ""
    @Prop({ default: null })
    private groupId!: string

    created() {
        this.getIncomeAndOutgo()
    }

    private getIncomeAndOutgo() {
        this.$apiClient.doRequest('get', `/admin/income_and_outgo/agri_field_groups?start_date=${this.startDateInput}&end_date=${this.endDateInput}`).then((ret:AgriFieldGroupIncomeOutgoResponse) => {
            this.responseItems = (toCamel(ret) as AgriFieldGroupIncomeOutgo).outlines
            this.$parent.$data.startDate = this.startDateInput
            this.$parent.$data.endDate = this.endDateInput
        })
    }

    private openAgriFieldGroupModal(groupId: string) {
        this.showAgriFieldGroupModal = true
        this.selectedGroupId = groupId
        this.$root.$emit('header-inert', true);
    }

    /**
     * モーダルからのコールバック
     */
    private checkClose(bool: boolean) { 
        if (bool) {
            this.showAgriFieldGroupModal = false
            // 再描画
            this.getIncomeAndOutgo()
        } else {
            this.showAgriFieldGroupModal = true
        }
        this.$root.$emit('header-inert', false);
    }

    /**
     * キャンセルはモーダルを閉じるのみ(再描画しない)
     */
    private cancelClose() { 
        this.showAgriFieldGroupModal = false
        this.$root.$emit('header-inert', false);
    }

    /**
     * 圃場毎の行データ。10a単位換算はここで行われる
     */
    get incomeAndOutgoItems() {
        return this.responseItems.map(i => (
            {
                agriFieldGroupId: i.agriFieldGroupId,
                agriFieldGroupName: i.agriFieldGroupName,
                agriFieldCount: i.agriFieldCount,
                plantedArea: i.plantedArea,
                income: this.apply10aView(i.income, i.plantedArea),
                workersOutgo: this.apply10aView(i.workersOutgo, i.plantedArea),
                machinesOutgo: this.apply10aView(i.machinesOutgo, i.plantedArea),
                chemicalsOutgo: this.apply10aView(i.chemicalsOutgo, i.plantedArea),
                fertilizersOutgo: this.apply10aView(i.fertilizersOutgo, i.plantedArea),
                materialsOutgo: this.apply10aView(i.materialsOutgo, i.plantedArea),
                seedlingsOutgo: this.apply10aView(i.seedlingsOutgo, i.plantedArea),
                outgo: this.apply10aView(i.outgo, i.plantedArea),
                total: this.apply10aView(i.total, i.plantedArea),
                disposalAmount: this.apply10aView(i.disposalAmount,i.plantedArea)
            }
        ))
    }

    /**
     * 10a単位の計算
     * @param amount 
     * @param plantedArea 
     */
    apply10aView(amount: number, plantedArea: number) {
        return this.is10aView? (10/plantedArea) * amount | 0 : amount
    }

    /**
     * 10a単位ビューへの切り替え
     */
    change10aView(is10aView: boolean) {
        this.is10aView = is10aView
        // 現在のソートキーに合わせたソートメソッドを呼ぶ
        const execSort = {
            income: this.sortByIncome,
            outgo: this.sortByOutgo,
            total: this.sortByAll,
            crops: () => { return }
        }
        execSort[this.currentSortKey](false)
    }

    /**
     * グラフビューへの切り替え
     */
    changeChartView(isChartView: boolean) {
        this.isChartView = isChartView
    }

    sortByIncome(isSwitchAsc?: boolean) {
        this.currentSortKey = 'income'
        // 昇順/降順の切替
        this.sortByIncomeIsAsc = isSwitchAsc ? !this.sortByIncomeIsAsc : this.sortByIncomeIsAsc
        this.responseItems.sort((a, b) => {
            const valueA = this.apply10aView(a.income, a.plantedArea)
            const valueB = this.apply10aView(b.income, b.plantedArea)
            return this.sortByIncomeIsAsc ? valueA - valueB : valueB - valueA
        })
    }

    sortByOutgo(isSwitchAsc?: boolean) {
        this.currentSortKey = 'outgo'
        // 昇順/降順の切替
        this.sortByOutgoIsAsc = isSwitchAsc ? !this.sortByOutgoIsAsc : this.sortByOutgoIsAsc
        this.responseItems.sort((a, b) => {
            const valueA = this.apply10aView(a.outgo, a.plantedArea)
            const valueB = this.apply10aView(b.outgo, b.plantedArea)
            return this.sortByOutgoIsAsc ? valueA - valueB : valueB - valueA
        })
    }

    sortByAll(isSwitchAsc?: boolean) {
        this.currentSortKey = 'total'
        // 昇順/降順の切替
        this.sortByAllIsAsc = isSwitchAsc ? !this.sortByAllIsAsc : this.sortByAllIsAsc
        this.responseItems.sort((a, b) => {
            const valueA = this.apply10aView(a.total, a.plantedArea)
            const valueB = this.apply10aView(b.total, b.plantedArea)
            return this.sortByAllIsAsc ? valueA - valueB : valueB - valueA
        })
    }
    sortByField(isSwitchAsc?: boolean) {
        this.sortByFieldIsAsc = isSwitchAsc ? !this.sortByFieldIsAsc : this.sortByFieldIsAsc
        this.responseItems.sort((a, b) => {
            const valueA = a.agriFieldGroupName;
            const valueB = b.agriFieldGroupName;
            return this.sortByFieldIsAsc ? (valueA > valueB ? -1 : -1) : (valueA < valueB ? -1 : -1)
        })
    }
    sortByDisposal(isSwitchAsc?:boolean) {
        this.sortByDisposalIsAsc = isSwitchAsc ? !this.sortByDisposalIsAsc : this.sortByDisposalIsAsc
        this.responseItems.sort((a, b) => {
            const valueA = this.apply10aView(a.disposalAmount, a.plantedArea)
            const valueB = this.apply10aView(b.disposalAmount, b.plantedArea)
            return this.sortByDisposalIsAsc ? (valueA - valueB) : (valueB - valueA)
        })
    }

    exportCsv() {
        this.$apiClient.getRequest( `/admin/income_and_outgo/agri_field_groups/csv_export?start_date=${this.startDateInput}&end_date=${this.endDateInput}`).then((res: CsvDownload) => {
            this.$apiClient.csvDownload(res)
        })
    }

    openSideBar = () => {
        const isResponsive = window.matchMedia('(max-width: 1000px)').matches;
        if (!isResponsive) {
            return; 
        }
        if (document.body.classList.contains('sideopen')) {
            document.body.classList.remove('sideopen')
        } else {
            document.body.classList.add('sideopen')
        }
    }

    onSortChange() {
      switch (this.sortType) {
        case "field":
          this.sortByField(true)
          break
        case "income":
          this.sortByIncome(true)
          break
        case "outgo":
          this.sortByOutgo(true)
          break
        case "all":
          this.sortByAll(true)
          break
        case "disposal":
          this.sortByDisposal(true)
          break
      }
    }
}